/**
 * Errors the app could not handle, sent to the API's log (POST /v1/client-errors; reliability plan
 * §5.1): an uncaught error, an unhandled promise rejection, and a screen the router's error boundary
 * caught. Each becomes one `client_error` line beside the request lines of the same person, so an
 * engineer sees the stack a customer hit without asking for a screenshot. Bounded on this side too:
 * at most {@link MAX_REPORTS} a page load, the same message once, sent after a short pause so a burst
 * goes out together. The path is the page's own, never its query. Best effort: a report that cannot
 * be sent is dropped, never retried and never shown.
 */
import type { PaigeClient } from '@paige/api-client';
import { currentAnalyticsTenant } from './events';

export type ClientErrorKind = 'error' | 'rejection' | 'render' | 'scanner';

interface Report {
  message: string;
  stack?: string;
  path: string;
  release: string;
  userAgent: string;
  kind: ClientErrorKind;
}

/** Reports one page load may send; a screen in a crash loop stops here. */
export const MAX_REPORTS = 5;
const SEND_AFTER_MS = 1_000;
const MAX_MESSAGE = 500;
const MAX_STACK = 4_000;

let client: PaigeClient | null = null;
let release = 'dev';
let sent = 0;
let seen = new Set<string>();
let pending: Report[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function describe(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) {
    const message = `${reason.name}: ${reason.message}`.slice(0, MAX_MESSAGE);
    return reason.stack ? { message, stack: reason.stack.slice(0, MAX_STACK) } : { message };
  }
  return { message: String(reason ?? 'unknown').slice(0, MAX_MESSAGE) };
}

async function send(): Promise<void> {
  timer = null;
  const batch = pending;
  pending = [];
  if (!client) return;
  const tenant = currentAnalyticsTenant();
  for (const body of batch) {
    try {
      await client.POST('/client-errors', {
        body,
        keepalive: true,
        ...(tenant ? { params: { header: { 'Paige-Tenant': tenant } } } : {}),
      });
    } catch {
      // Best effort: the report is lost, the page is not disturbed.
    }
  }
}

/** Queues one report; past the page's allowance, or a message already sent, it is dropped. */
export function reportClientError(reason: unknown, kind: ClientErrorKind): void {
  const { message, stack } = describe(reason);
  if (sent >= MAX_REPORTS || seen.has(message)) return;
  sent += 1;
  seen.add(message);
  pending.push({
    message,
    ...(stack ? { stack } : {}),
    path: window.location.pathname,
    release,
    userAgent: navigator.userAgent.slice(0, 300),
    kind,
  });
  timer ??= setTimeout(() => void send(), SEND_AFTER_MS);
}

/** Starts reporting: the window's uncaught errors and rejections from now on; returns the unsubscribe. */
export function watchClientErrors(
  api: PaigeClient,
  build: string,
  win: Window = window,
): () => void {
  client = api;
  release = build;
  const onError = (e: ErrorEvent) => reportClientError(e.error ?? e.message, 'error');
  const onRejection = (e: PromiseRejectionEvent) => reportClientError(e.reason, 'rejection');
  win.addEventListener('error', onError);
  win.addEventListener('unhandledrejection', onRejection);
  return () => {
    win.removeEventListener('error', onError);
    win.removeEventListener('unhandledrejection', onRejection);
  };
}

/** Sends what is queued now (tests; a page going away). */
export async function flushClientErrors(): Promise<void> {
  if (timer) clearTimeout(timer);
  await send();
}

export function resetClientErrorsForTests(): void {
  if (timer) clearTimeout(timer);
  client = null;
  release = 'dev';
  sent = 0;
  seen = new Set();
  pending = [];
  timer = null;
}
